import React, { useState } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Plus, Trash2, ChevronDown, ChevronUp, Wrench } from 'lucide-react'

interface ToolDef {
  name: string
  description: string
  url: string
  method: string
  headers: Record<string, string>
  parameters: Record<string, { type: string; description?: string; required?: boolean }>
}

interface SkillToolEditorProps {
  tools: ToolDef[]
  onChange: (tools: ToolDef[]) => void
  maxTools?: number
}

const EMPTY_TOOL: ToolDef = {
  name: '',
  description: '',
  url: '',
  method: 'GET',
  headers: {},
  parameters: {},
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

export const SkillToolEditor: React.FC<SkillToolEditorProps> = ({
  tools,
  onChange,
  maxTools = 10,
}) => {
  const [expanded, setExpanded] = useState<number | null>(tools.length > 0 ? 0 : null)

  const addTool = () => {
    if (tools.length >= maxTools) return
    const newTools = [...tools, { ...EMPTY_TOOL }]
    onChange(newTools)
    setExpanded(newTools.length - 1)
  }

  const updateTool = (index: number, updates: Partial<ToolDef>) => {
    onChange(tools.map((t, i) => i === index ? { ...t, ...updates } : t))
  }

  const removeTool = (index: number) => {
    onChange(tools.filter((_, i) => i !== index))
    setExpanded(null)
  }

  // Key-value pair helpers for headers
  const addHeader = (toolIndex: number) => {
    const tool = tools[toolIndex]
    const newHeaders = { ...tool.headers, '': '' }
    updateTool(toolIndex, { headers: newHeaders })
  }

  const updateHeader = (toolIndex: number, oldKey: string, newKey: string, value: string) => {
    const tool = tools[toolIndex]
    const entries = Object.entries(tool.headers || {}).map(([k, v]) =>
      k === oldKey ? [newKey, value] : [k, v]
    )
    updateTool(toolIndex, { headers: Object.fromEntries(entries) })
  }

  const removeHeader = (toolIndex: number, key: string) => {
    const tool = tools[toolIndex]
    const { [key]: _, ...rest } = tool.headers
    updateTool(toolIndex, { headers: rest })
  }

  // Parameter helpers
  const addParam = (toolIndex: number) => {
    const tool = tools[toolIndex]
    const newParams = { ...tool.parameters, '': { type: 'string' } }
    updateTool(toolIndex, { parameters: newParams })
  }

  const updateParam = (toolIndex: number, oldKey: string, newKey: string, spec: { type: string; description?: string; required?: boolean }) => {
    const tool = tools[toolIndex]
    const entries = Object.entries(tool.parameters || {}).map(([k, v]) =>
      k === oldKey ? [newKey, spec] : [k, v]
    )
    updateTool(toolIndex, { parameters: Object.fromEntries(entries) })
  }

  const removeParam = (toolIndex: number, key: string) => {
    const tool = tools[toolIndex]
    const { [key]: _, ...rest } = tool.parameters
    updateTool(toolIndex, { parameters: rest })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          HTTP tools the agent can call. Max {maxTools} per skill.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addTool}
          disabled={tools.length >= maxTools}
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Tool
        </Button>
      </div>

      {tools.length === 0 && (
        <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-xl">
          <Wrench className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No tools yet. Add one to get started.</p>
        </div>
      )}

      {tools.map((tool, index) => (
        <div key={index} className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Card header — always visible */}
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
            onClick={() => setExpanded(expanded === index ? null : index)}
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
              {tool.name || `Tool ${index + 1}`}
              {tool.method && <span className="ml-2 text-xs text-gray-400">{tool.method}</span>}
            </span>
            <div className="flex items-center gap-2">
              <span
                className="text-red-400 hover:text-red-600"
                onClick={(e) => { e.stopPropagation(); removeTool(index) }}
              >
                <Trash2 className="w-4 h-4" />
              </span>
              {expanded === index ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </button>

          {/* Expanded card body */}
          {expanded === index && (
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Tool name"
                  value={tool.name}
                  onChange={(e) => updateTool(index, { name: e.target.value })}
                />
                <select
                  className="h-10 px-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 cursor-pointer"
                  value={tool.method}
                  onChange={(e) => updateTool(index, { method: e.target.value })}
                >
                  {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <Input
                placeholder="Description"
                value={tool.description}
                onChange={(e) => updateTool(index, { description: e.target.value })}
              />

              <Input
                placeholder="URL (e.g. https://api.example.com/endpoint)"
                value={tool.url}
                onChange={(e) => updateTool(index, { url: e.target.value })}
              />

              {/* Headers */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 uppercase">Headers</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => addHeader(index)}>
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
                {Object.entries(tool.headers || {}).map(([hKey, hVal], hIdx) => (
                  <div key={hIdx} className="flex gap-2 mb-2">
                    <Input
                      className="flex-1"
                      placeholder="Header name"
                      value={hKey}
                      onChange={(e) => updateHeader(index, hKey, e.target.value, hVal)}
                    />
                    <Input
                      className="flex-1"
                      placeholder="Value"
                      value={hVal}
                      onChange={(e) => updateHeader(index, hKey, hKey, e.target.value)}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeHeader(index, hKey)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Parameters */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 uppercase">Parameters</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => addParam(index)}>
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
                {Object.entries(tool.parameters || {}).map(([pKey, pSpec], pIdx) => (
                  <div key={pIdx} className="flex gap-2 mb-2">
                    <Input
                      className="flex-1"
                      placeholder="Parameter name"
                      value={pKey}
                      onChange={(e) => updateParam(index, pKey, e.target.value, pSpec)}
                    />
                    <select
                      className="h-10 w-28 px-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                      value={pSpec.type || 'string'}
                      onChange={(e) => updateParam(index, pKey, pKey, { ...pSpec, type: e.target.value })}
                    >
                      <option value="string">string</option>
                      <option value="number">number</option>
                      <option value="boolean">boolean</option>
                      <option value="object">object</option>
                    </select>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeParam(index, pKey)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default SkillToolEditor
